import { useAuthStore } from "@/modules/auth/store"
import { NetworkMetrics } from "../metrics/index"
import router from "@/router"

// Constants
const TOKENLESS_ROUTES = {
  LOGIN: "login",
  SIGNUP: "signup",
  INVITE_ACCEPT: "invite-accept",
}

const REFRESH_TOKEN_PATH = "/token"

let isRefreshing = false
let pendingQueue = []

// the queue is open only while the refresh call is in flight: this ends it and settles everything in it
const processPendingQueue = (error, token = null) => {
  isRefreshing = false
  const queue = pendingQueue
  pendingQueue = []
  queue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
}

const addToPendingQueue = () => new Promise((resolve, reject) => pendingQueue.push({ resolve, reject }))

// the token a request went out with, whether its headers are AxiosHeaders or a plain object
const sentAuthorization = (headers) => (headers?.get ? headers.get("Authorization") : headers?.Authorization)

// a completely new axios request with the updated config
const resend = (axiosInstance, originalRequest, token) =>
  axiosInstance({
    ...originalRequest,
    headers: {
      ...originalRequest.headers,
      Authorization: `Bearer ${token}`,
    },
    _retry: true, // Mark as retried to prevent infinite loops
    _fromAuthRefresh: true,
  })

const isTokenlessRoute = (routeName) => {
  return !Object.values(TOKENLESS_ROUTES).includes(routeName)
}

const validateTokenResponse = (response) => {
  return response && typeof response === "object" && typeof response.token === "string" && (response.expiresIn === undefined || typeof response.expiresIn === "number")
}

const isRefreshTokenRequest = (url) => {
  try {
    const pathname = new URL(url, window.location.origin).pathname
    return pathname.endsWith(REFRESH_TOKEN_PATH)
  } catch {
    console.error("Invalid URL in isRefreshTokenRequest:", url)
    return false
  }
}

const handleQueuedRequest = async (axiosInstance, originalRequest) => {
  try {
    const token = await addToPendingQueue()
    console.log("Queued request proceeding with new token", {
      url: originalRequest.url,
    })

    // we have a new refresh token - let's fix up the original request and
    // execute it (for the first time)
    return resend(axiosInstance, originalRequest, token)
  } catch (err) {
    console.error("Queued request failed:", err)
    return Promise.reject(err)
  }
}

const performTokenRefresh = async (axiosInstance, originalRequest, authStore) => {
  const metrics = NetworkMetrics.getInstance()
  const requestContext = {
    triggeredBy: originalRequest.url,
    queueLength: pendingQueue.length,
  }

  console.log("Starting token refresh", requestContext)

  originalRequest._retry = true // we are going to retry this request
  isRefreshing = true // we are now going to try and refresh the access token

  let token
  try {
    const response = await authStore.refreshToken()

    if (!validateTokenResponse(response)) {
      throw new Error("Invalid token response format")
    }

    token = response.token
    console.log("Token refresh successful")
  } catch (err) {
    // we weren't able to get a new token
    processPendingQueue(err) // cancel all the other requests that have come in, before anything else can throw

    console.error("Token refresh failed")
    metrics.logError(err, "token_refresh", Date.now(), requestContext)
    return Promise.reject(err) // and reject the promise
  }

  // now that we have a new token again,
  // lets's process any of the other calls that may have come in in the mean time
  processPendingQueue(null, token)

  return resend(axiosInstance, originalRequest, token)
}

export function setupAuthInterceptors(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        const authStore = useAuthStore()
        const originalRequest = error.config
        const currentRoute = router.currentRoute.value.name

        if (isTokenlessRoute(currentRoute)) {
          if (originalRequest._retry || isRefreshTokenRequest(error.config.url)) {
            console.log("Token refresh failed - logging out", {
              isRetry: originalRequest._retry,
              url: error.config.url,
            })
            // Make sure to clear the pending queue with the error
            processPendingQueue(error)
            await authStore.logout({ loggedOutReason: "You have been logged out." })
            return Promise.reject(error)
          } else {
            // sent with an older token than requests now go out with: a refresh already happened, so just re-send.
            // Compare with the default header, not the auth store: on share pages it holds the public token.
            const sent = sentAuthorization(originalRequest.headers)
            const current = axiosInstance.defaults.headers.common?.Authorization
            if (current && sent && sent !== current) {
              return resend(axiosInstance, originalRequest, current.replace(/^Bearer /, ""))
            }
            return isRefreshing ? handleQueuedRequest(axiosInstance, originalRequest) : performTokenRefresh(axiosInstance, originalRequest, authStore)
          }
        }
      }
      return Promise.reject(error)
    }
  )

  return axiosInstance
}
